import {
    flatten,
    isMatch,
    map,
    remove
} from 'lodash';

export interface RegisteredTargetGroup {
    groupName: string;
    targetsInGroup: string[];
}

export interface IChecklistContext {
    readonly registeredTargets: string[];
    readonly registeredGroups: RegisteredTargetGroup[];
    registerTarget(id: string, group?: string): () => void;
    registerGroup(group: string): () => void;
    registerCriteria(id: string, criteria: Record<string, unknown>): () => void;
    updateValidation(id: string, valid: boolean): void;
    getValidation(id: string): boolean;
    getFirstInvalidTarget(): string | null;
    getTargetByCriteria(criteria: Record<string, unknown>): string | null;
}

export enum ChecklistContextErrors {
    MustBeRegisteredToUpdate = 'Target must be registered before updating validation',
    TargetNotRegistered = 'Target was not registered'
}

/**
 * Context for validation checklist functionality
 * Caveats
 * - Works best when all the targets are rendered at once
 * - Ordered in terms of mounting/rendering order within the parent
 *   - This means that if some of the check list targets are conditionally rendered,
 *   the mounting/rendering order will place those at the end when they are available
 *   - Best workaround so far is to only render all of the targets once all targets are
 *   ready to be rendered
 *
 * New workarounds
 * - Grouping
 * By default, all targets are placed in the default group.
 * However, if you would like to reserve a place for targets,
 * you can register a group ahead of time and ensure that the targets will be ordered by that group.
 */
export class ChecklistContext implements IChecklistContext {
    static readonly DEFAULT_GROUP = 'default';

    targets: Map<string, boolean> = new Map<string, boolean>();
    targetGroups: Map<string, string[]> = new Map<string, string[]>();
    targetCriteria: Map<string, Record<string, unknown>> = new Map<string, Record<string, unknown>>();

    constructor(groups: string[] = []) {
        groups.forEach(group => this.registerGroup(group));
    }

    get registeredTargets(): string[] {
        return flatten(Array.from(this.targetGroups.values()));
    }

    get registeredGroups(): RegisteredTargetGroup[] {
        return Array
            .from(this.targetGroups.entries())
            .map(([groupName, targetsInGroup]) => ({
                groupName,
                targetsInGroup
            }));
    }

    registerTarget(id: string, group: string = ChecklistContext.DEFAULT_GROUP): () => void {
        this.targets.set(id, false);

        const targetGroup = (this.targetGroups.get(group) ?? []);
        targetGroup.push(id);
        this.targetGroups.set(group, targetGroup);

        return () => {
            this.targets.delete(id);
            remove(targetGroup, target => target === id);
        };
    }

    registerGroup(group: string): () => void {
        this.targetGroups.set(group, []);

        return () => {
            const groupMembers = this.targetGroups.get(group) ?? [];
            this.targetGroups.delete(group);

            if (!groupMembers.length) {
                return;
            }

            const defaultGroup = (this.targetGroups.get(ChecklistContext.DEFAULT_GROUP) ?? []);
            defaultGroup.push(...groupMembers);
            this.targetGroups.set(ChecklistContext.DEFAULT_GROUP, defaultGroup);
        }
    }

    registerCriteria(id: string, criteria: Record<string, unknown>): () => void {
        this.targetCriteria.set(id, criteria);

        return () => {
            this.targetCriteria.delete(id);
        };
    }

    updateValidation(id: string, valid: boolean): void {
        if (!this.targets.has(id)) {
            throw new Error(ChecklistContextErrors.MustBeRegisteredToUpdate);
        }

        this.targets.set(id, valid);
    }

    getValidation(id: string): boolean {
        if (!this.targets.has(id)) {
            throw new Error(ChecklistContextErrors.TargetNotRegistered);
        }

        return this.targets.get(id)!;
    }

    getFirstInvalidTarget(): string | null {
        const entriesSortedByGroups = this.getTargetsByGroups();
        const firstInvalidEntry = entriesSortedByGroups.find(([_key, valid]) => !valid);
        return firstInvalidEntry?.[0] ?? null;
    }

    getTargetByCriteria(criteria: Record<string, unknown>): string | null {
        const entries = Array.from(this.targetCriteria.entries());
        const firstMatch = entries.find(([_key, targetCriteria]) => isMatch(targetCriteria, criteria));

        return firstMatch?.[0] ?? null;
    }

    private getTargetsByGroups(): Array<[string, boolean]> {
        const groupsOfTargets = Array.from(this.targetGroups.values());
        return flatten(
            map(
                groupsOfTargets,
                targetsInGroup => targetsInGroup.map(target => [target, this.targets.get(target) ?? false])
            )
        ) as Array<[string, boolean]>;
    }
}



// WEBPACK FOOTER //
// ./src/components/checklist/checklist-context.ts